import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'

import { type INativeInteractor } from '../module/nativeInteractor/nativeInteractor'

import {
  type DataResponse,
  type User,
  type UpdateResponse,
  type LearningKeyword,
  type SearchedExam,
  type SurveySubmitRequest,
  type SuggestBooks,
  type StatsOption,
  type StatsData,
  type UserSurvey,
  type Dday,
  type ErrorResponse,
} from './interfaces/sconnStudyData'

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

const TOKEN_REFRESH_THRESHOLD_SECONDS = 30
const EXPIRED_TOKEN_REASON = 'The incoming token has expired'

const logAxiosInterceptorError = (label: string, error: AxiosError) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(label, error)
    return
  }

  console.error(label, {
    message: error.message,
    status: error.response?.status,
    code: error.code,
  })
}

export default class SconnStudyDataClient {
  private readonly _httpClient: AxiosInstance
  private _expires: number | null = null
  #accessToken: string | null = null

  constructor(private readonly nativeInteractor: INativeInteractor) {
    this._httpClient = axios.create({
      baseURL: `${process.env.REACT_APP_API_BASE_URL}/${process.env.REACT_APP_API_VERSION}`,
      headers: { 'Content-Type': 'application/json' },
    })

    this._httpClient.interceptors.request.use(
      async (config) => {
        if (this.shouldRefreshToken()) {
          await this.refreshAccessToken()
        }

        this.setAuthorizationHeader(config)
        return config
      },
      async (error: AxiosError) => {
        logAxiosInterceptorError('Request Error Interceptor:', error)
        return await Promise.reject(error)
      },
    )

    this._httpClient.interceptors.response.use(
      (response) => {
        if (this.shouldRefreshToken()) {
          void this.refreshAccessToken()
        }

        response.headers.Authorization = `Bearer ${this.#accessToken}`
        return response
      },
      async (error: AxiosError<ErrorResponse>) => {
        const config = error.config as RetriableRequestConfig | undefined
        if (config && !config._retry && this.isTokenExpiredError(error)) {
          config._retry = true

          if (await this.refreshAccessToken()) {
            this.setAuthorizationHeader(config)
            return await this._httpClient(config)
          }
        }

        logAxiosInterceptorError('Response Error Interceptor:', error)
        return await Promise.reject(error)
      },
    )
  }

  get expires(): number | null {
    return this._expires
  }

  set expires(exp: number) {
    this._expires = exp
  }

  /**
   * 학습키워드 트리 조회.
   * V2에서 GET /list/cert, /list/uc, /list/high, /list/middle 은 모두 본 엔드포인트와
   * 동일한 학습키워드 트리를 반환하므로 /list/usertype 하나로 통일하고 나머지 메서드는 제거함.
   */
  async getLearningKeywords() {
    return await this._httpClient.get<DataResponse<LearningKeyword[]>>('/list/usertype')
  }

  async getUserInformation() {
    return await this._httpClient.get<DataResponse<User>>('/user')
  }

  async getUserSuggests() {
    return await this._httpClient.get<DataResponse<SuggestBooks>>('/user/suggests')
  }

  async getUserStatistics(option: StatsOption) {
    return await this._httpClient.get<DataResponse<StatsData>>('/user/stats', {
      params: option,
    })
  }

  async getUserSurvey() {
    return await this._httpClient.get<DataResponse<UserSurvey>>('/user/survey')
  }

  async postNickname(nickName: string) {
    return await this._httpClient.post<UpdateResponse>('/user/nickname', {
      nickName,
    })
  }

  async validateNickname(nickName: string) {
    return await this._httpClient.post<UpdateResponse>('/u/nickname/valid', {
      nickName,
    })
  }

  async postConsentAllow(allow: boolean) {
    return await this._httpClient.post<UpdateResponse>('/user/consent', {
      allow,
    })
  }

  async postSurvey(survey: SurveySubmitRequest) {
    return await this._httpClient.post<UpdateResponse>('/user/survey', survey)
  }

  /**
   * 시험 이름 검색.
   * V2에서 전체 목록(/list/cert)을 받아 프론트에서 필터링하던 방식이 서버 검색으로 대체됨.
   * V1의 계층 검색(/search/uc, /search/uc/department, /search/middle/class,
   * /search/high/class)은 폐기되어 메서드 제거함.
   */
  async searchExams(searchWord: string) {
    return await this._httpClient.post<DataResponse<SearchedExam[]>>('/search/cert', {
      searchWord,
    })
  }

  async getUserDday() {
    return await this._httpClient.get<DataResponse<Dday>>('/user/dday')
  }

  async postUserDday(dday: Dday) {
    return await this._httpClient.post<UpdateResponse>('/user/dday', dday)
  }

  async putGoalDaily(timeLength: number, offset: number) {
    return await this._httpClient.put<UpdateResponse>('/user/goal/daily', { timeLength, offset })
  }

  async postGoalDaily(timeLength: number, offset: number) {
    return await this._httpClient.post<UpdateResponse>('/user/goal/daily', { timeLength, offset })
  }

  setAccessToken(accessToken: string) {
    this.#accessToken = accessToken
  }

  private shouldRefreshToken() {
    return this.expires !== null && this.expires - Date.now() / 1000 < TOKEN_REFRESH_THRESHOLD_SECONDS
  }

  private async refreshAccessToken() {
    try {
      const accessToken = await this.nativeInteractor.getNativeToken()
      if (!accessToken) {
        console.log('accessToken null from native')
        return false
      }

      this.setAccessToken(accessToken)
      this.expires = this.decodeExp(accessToken)
      return true
    } catch (error) {
      console.log('callNativeFunction, error', error)
      return false
    }
  }

  private setAuthorizationHeader(config: InternalAxiosRequestConfig) {
    if (this.#accessToken !== null) {
      config.headers = config.headers ?? new AxiosHeaders()
      config.headers.Authorization = `Bearer ${this.#accessToken}`
    }
  }

  private isTokenExpiredError(error: AxiosError<ErrorResponse>) {
    return (
      error.response?.status === 401 ||
      error.response?.statusText === 'Unauthorized' ||
      error.response?.data.reason === EXPIRED_TOKEN_REASON ||
      error.message === EXPIRED_TOKEN_REASON
    )
  }

  private decodeExp(acessToken: string | null): number {
    if (acessToken === null) return 0

    const payload = acessToken.substring(acessToken.indexOf('.') + 1, acessToken.lastIndexOf('.'))
    const decoded = window.atob(payload)
    const parsed = JSON.parse(decoded)

    return parsed.exp ? parsed.exp : 0
  }
}
